/**
 * Shared utilities for parsing inline Ask references used in markdown:
 * - [hexVis:<cellId>]
 * - [hexProject:<projectId>]
 * - [hexPlainCodeProject:<projectId>]
 * - [hexCell:<cellId>] (legacy tag for cell references)
 * - [hexTask:<taskId>]
 * - [hexFile:<fileId>] (downloadable project file references)
 *
 * Keep this in sync with the agent prompts.
 *
 * Adding a new reference tag type? Make sure to:
 * 1. Declare a constant here and extend the regex below.
 * 2. Teach the remark plugin in remarkHexInlineReferences.ts how to handle the tag.
 * 3. Allow the tag's attributes in MarkdownSanitizeSchema.ts.
 * 4. Render the tag inside InlineMarkdownRenderer.tsx.
 * 5. Update downstream renderers (sharedMessageFormatting.ts, agentSlackbotUtils.ts, MCP formatters, etc.) so secondary channels understand the new tag.
 * 6. Cover the parser + renderer behavior with tests since these paths are too easy to break.
 */

import { HexId, StaticCellId } from "../idTypeBrands.js";

export const HEX_VIS_TAG = "hexVis";
export const HEX_PLAIN_CODE_PROJECT_TAG = "hexPlainCodeProject";
export const HEX_PROJECT_TAG = "hexProject";
export const HEX_CELL_TAG = "hexCell";
export const HEX_TASK_TAG = "hexTask";
export const HEX_FILE_TAG = "hexFile";
export const UNRESOLVED_PROJECT_REFERENCE_ID = "unresolved";

// Separator used for composite ids that include an "external" app/project prefix.
// Example: `<staticCellId>@<hexId>`
export const HEX_EXTERNAL_CELL_REFERENCE_SEPARATOR = "@";

/**
 * Parse a display-form cell reference ID that may be an external (cross-project)
 * reference in the format `<staticCellId>@<hexId>`.
 */
export function parseExternalCellReferenceId(
  referenceId: string,
): { staticCellId: StaticCellId; hexId: HexId } | null {
  const separatorIndex = referenceId.indexOf(
    HEX_EXTERNAL_CELL_REFERENCE_SEPARATOR,
  );
  if (separatorIndex <= 0 || separatorIndex === referenceId.length - 1) {
    return null;
  }
  return {
    staticCellId: referenceId.slice(0, separatorIndex) as StaticCellId,
    hexId: referenceId.slice(separatorIndex + 1) as HexId,
  };
}

const ALL_REFERENCE_TAGS = [
  HEX_VIS_TAG,
  HEX_PLAIN_CODE_PROJECT_TAG,
  HEX_PROJECT_TAG,
  HEX_CELL_TAG,
  HEX_TASK_TAG,
  HEX_FILE_TAG,
].join("|");

/**
 * Models regularly emit references with stray horizontal whitespace inside the
 * brackets (`[hexVis: <id>]`, `[ hexVis:<id> ]`). Those are unambiguously
 * references, so every separator tolerates padding rather than leaving the tag
 * to render as dead literal text. Newlines stay excluded so a match can never
 * span markdown lines.
 */
const PAD = "[ \\t]*";

const buildReferencePattern = (tagPattern: string): string =>
  `\\[${PAD}(${tagPattern})${PAD}:${PAD}([a-zA-Z0-9_@-]{1,128})${PAD}(?::${PAD}([^\\]]+))?${PAD}\\]`;

export const getReferenceRegex = ({
  allowWrappingBackticks = true,
}: {
  /**
   * Matches an optional backtick on either side of the reference (e.g.
   * `` `[hexCell:C01]` ``). Consumers that already run after markdown parsing
   * see code spans as separate nodes and should turn this off.
   */
  allowWrappingBackticks?: boolean;
} = {}): RegExp => {
  const pattern = buildReferencePattern(ALL_REFERENCE_TAGS);
  return new RegExp(allowWrappingBackticks ? `\`?${pattern}\`?` : pattern, "g");
};

/**
 * Matcher for a single reference tag (e.g. only `[hexProject:<id>]`), sharing
 * the id shape and whitespace tolerance of {@link getReferenceRegex}.
 *
 * Capture groups: 1) tag, 2) id, 3) optional encoded label.
 */
export const getSingleTagReferenceRegex = (tag: string): RegExp =>
  new RegExp(buildReferencePattern(tag), "g");

export type InlineReferenceTextChunk = {
  type: "MARKDOWN";
  content: string;
};

export type InlineReferenceCellChunk = {
  type: "CELL_REFERENCE";
  /** This may be a staticCellId or a cellId (pre-January 2026) */
  cellId: string;
  /**
   * Optional, decoded label segment from `[hexCell:<id>:<encodedLabel>]`.
   *
   * Consumers should treat it as a best-effort display hint (e.g. for missing cells).
   */
  label?: string;
  referenceTag: typeof HEX_VIS_TAG | typeof HEX_CELL_TAG;
};

export type InlineReferenceProjectChunk = {
  type: "PROJECT_REFERENCE";
  projectRef: string;
};

export type InlineReferencePlainCodeProjectChunk = {
  type: "PLAIN_CODE_PROJECT_REFERENCE";
  projectId: string;
};

export type InlineReferenceTaskChunk = {
  type: "TASK_REFERENCE";
  taskId: string;
};

export type InlineReferenceFileChunk = {
  type: "FILE_REFERENCE";
  /** The id of the project file to make downloadable. */
  fileId: string;
};

export type InlineReferenceChunk =
  | InlineReferenceTextChunk
  | InlineReferenceCellChunk
  | InlineReferenceProjectChunk
  | InlineReferencePlainCodeProjectChunk
  | InlineReferenceTaskChunk
  | InlineReferenceFileChunk;

/**
 * Splits markdown into chunks of plain text and tagged references
 * (e.g. `[hexCell:<id>]`).
 */
export function parseInlineReferences(
  markdown: string,
): InlineReferenceChunk[] {
  const chunks: InlineReferenceChunk[] = [];

  const referenceRegex = getReferenceRegex();
  referenceRegex.lastIndex = 0;

  let lastIndex = 0;
  let match: RegExpExecArray | null;
  // Add text before the match, if any
  while ((match = referenceRegex.exec(markdown)) != null) {
    if (match.index > lastIndex) {
      const textContent = markdown.slice(lastIndex, match.index);
      chunks.push({ type: "MARKDOWN", content: textContent });
    }

    const patternType = match[1];
    const id = match[2];
    const rawLabel = match[3];
    if (!patternType || !id) {
      continue;
    }

    if (patternType === HEX_VIS_TAG || patternType === HEX_CELL_TAG) {
      const referenceTag =
        patternType === HEX_VIS_TAG ? HEX_VIS_TAG : HEX_CELL_TAG;
      const label = decodeInlineReferenceLabel(rawLabel);
      chunks.push({
        type: "CELL_REFERENCE",
        cellId: id,
        ...(label != null ? { label } : {}),
        referenceTag,
      });
    } else if (patternType === HEX_PROJECT_TAG) {
      chunks.push({
        type: "PROJECT_REFERENCE",
        projectRef: id,
      });
    } else if (patternType === HEX_PLAIN_CODE_PROJECT_TAG) {
      chunks.push({
        type: "PLAIN_CODE_PROJECT_REFERENCE",
        projectId: id,
      });
    } else if (patternType === HEX_TASK_TAG) {
      chunks.push({
        type: "TASK_REFERENCE",
        taskId: id,
      });
    } else if (patternType === HEX_FILE_TAG) {
      chunks.push({
        type: "FILE_REFERENCE",
        fileId: id,
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last match
  if (lastIndex < markdown.length) {
    chunks.push({ type: "MARKDOWN", content: markdown.slice(lastIndex) });
  }

  // If nothing matched, return the full markdown as a single text chunk
  if (chunks.length === 0) {
    chunks.push({ type: "MARKDOWN", content: markdown });
  }

  return chunks;
}

export function encodeInlineReferenceLabel(label: string): string {
  return encodeURIComponent(label);
}

export function decodeInlineReferenceLabel(
  encodedLabel: string | undefined,
): string | undefined {
  // Encoded labels never legitimately contain raw whitespace (spaces arrive as
  // `%20`), so surrounding padding is always an artifact of a sloppy reference.
  const trimmed = encodedLabel?.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    return decodeURIComponent(trimmed);
  } catch {
    return trimmed;
  }
}
